
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Coins, CreditCard, Sparkles, ArrowRight } from "lucide-react";
import { useState, useEffect } from "react";
import { Book } from "@/types/book";
import { Bundle } from "@/types/bundle";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useIOSDetection } from "@/hooks/use-ios-detection";
import { useCurrencyDetection } from "@/hooks/use-currency-detection";
import { BundleBookGrid } from "./purchase-modal/BundleBookGrid";
import { PurchaseBreakdown } from "./purchase-modal/PurchaseBreakdown";
import { CreditDiscountSection } from "./purchase-modal/CreditDiscountSection";
import { PaymentButtons } from "@/components/ui/payment-buttons";
import { motion, AnimatePresence } from "framer-motion";

interface PurchaseConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseType: 'bundle' | 'individual' | 'early_access';
  bundle?: Bundle | null;
  book?: Book | null;
  bundleBooks?: Book[];
  originalPrice: number;
  userCredits: number;
  creditsEarned: number;
  onPurchase: () => void;
  bundleId?: number;
  earlyAccessBookId?: number | null;
}

export function PurchaseConfirmationModal({
  isOpen,
  onClose,
  purchaseType,
  bundle,
  book,
  bundleBooks = [],
  originalPrice,
  userCredits,
  creditsEarned,
  onPurchase,
  bundleId,
  earlyAccessBookId
}: PurchaseConfirmationModalProps) {
  const isMobile = useIsMobile();
  const isIOS = useIOSDetection();
  const { formatPrice } = useCurrencyDetection();
  const [creditsToUse, setCreditsToUse] = useState(0);
  const [finalPrice, setFinalPrice] = useState(originalPrice);

  // Calculate maximum credits that can be used (up to 80% of price)
  const maxCreditsUsable = Math.min(userCredits, Math.floor(originalPrice * 0.8));

  // Calculate credits earned based on purchase type and final amount paid
  const calculateCreditsEarned = (finalAmount: number) => {
    const baseCredits = Math.ceil(finalAmount * 0.1); // 10% rounded up
    
    // Individual books: just 10% rounded up (no minimum)
    if (purchaseType === 'individual') {
      return baseCredits;
    }
    
    // Bundles and early access: minimum 4 credits
    return Math.max(baseCredits, 4);
  };

  useEffect(() => {
    const discount = creditsToUse;
    const newFinalPrice = Math.max(0, originalPrice - discount);
    setFinalPrice(newFinalPrice);
  }, [creditsToUse, originalPrice]);

  // iOS-specific styles to handle viewport issues
  const getIOSStyles = () => {
    if (!isIOS) return {};
    
    return {
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)',
      minHeight: '100dvh', // Dynamic viewport height for better iOS support
    };
  };

  const getSavingsAmount = () => {
    if (purchaseType === 'bundle' && bundleBooks.length > 0) {
      const individualTotal = bundleBooks.length * 3.99; // Assuming $3.99 per book
      return individualTotal - finalPrice;
    }
    return 0;
  };

  const getModalTitle = () => {
    switch (purchaseType) {
      case 'bundle':
        return bundle?.title || 'Bundle Purchase';
      case 'early_access':
        return `Early Access: ${bundle?.title || 'Bundle'}`;
      case 'individual':
        return `Purchase: ${book?.title || 'Book'}`;
      default:
        return 'Confirm Purchase';
    }
  };

  const getModalSubtitle = () => {
    switch (purchaseType) {
      case 'bundle':
        return `Get ${bundleBooks.length} amazing books in this collection`;
      case 'early_access':
        return 'Pre-order the bundle and choose your early access book after purchase';
      case 'individual':
        return 'Add this book to your library';
      default:
        return '';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent 
            className={cn(
              "w-full h-full max-w-none max-h-none p-0 border-0 bg-slate-950/95 backdrop-blur-md",
              isIOS && "fixed inset-0"
            )}
            style={getIOSStyles()}
          >
            <DialogTitle className="sr-only">{getModalTitle()}</DialogTitle>
            
            <motion.div 
              className={cn(
                "flex flex-col h-full",
                isIOS && "min-h-screen"
              )}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              {/* Header */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.6 }}
                className={cn(
                  "flex items-center justify-between p-6 border-b border-white/10",
                  isIOS && "pt-safe-top"
                )}
              >
                <div>
                  <h2 className="text-2xl font-display font-bold text-white">{getModalTitle()}</h2>
                  <p className="text-white/70 mt-1">{getModalSubtitle()}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="text-white/70 hover:text-white hover:bg-white/10"
                >
                  <X className="w-6 h-6" />
                </Button>
              </motion.div>

              {/* Content */}
              <div className={cn(
                "flex-1 overflow-y-auto",
                isIOS && "pb-safe-bottom"
              )}>
                <div className="max-w-6xl mx-auto p-6 pb-12">
                  <div className={cn(
                    "grid gap-8",
                    isMobile ? "grid-cols-1" : "grid-cols-2"
                  )}>
                    {/* Left Column - Visual Content */}
                    <div className="space-y-6">
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                      >
                        {(purchaseType === 'bundle' || purchaseType === 'early_access') ? (
                          <BundleBookGrid 
                            books={bundleBooks}
                            bundle={bundle}
                            isEarlyAccess={purchaseType === 'early_access'}
                            selectedEarlyAccessBook={earlyAccessBookId}
                          />
                        ) : (
                          <div className="glass-card p-6">
                            <div className="flex items-center gap-4">
                              <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                                <img
                                  src={book?.cover_image_url || ''}
                                  alt={book?.title || ''}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div>
                                <h3 className="text-xl font-display font-semibold text-white">{book?.title}</h3>
                                <p className="text-white/70">{book?.author}</p>
                                {book?.year && (
                                  <p className="text-white/50 text-sm">{book.year}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </motion.div>

                      {/* Savings Highlight */}
                      {getSavingsAmount() > 0 && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.5, duration: 0.6 }}
                        >
                          <div className="glass-card p-4 border-emerald-500/30">
                            <div className="flex items-center gap-2 text-emerald-400 mb-2">
                              <Sparkles className="w-5 h-5" />
                              <span className="font-display font-semibold">Great Deal!</span>
                            </div>
                            <p className="text-white">
                              You're saving <span className="font-bold text-emerald-400">{formatPrice(getSavingsAmount())}</span> compared to buying individually
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </div>

                    {/* Right Column - Purchase Details */}
                    <div className="space-y-6">
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                      >
                        <PurchaseBreakdown
                          originalPrice={originalPrice}
                          creditsUsed={creditsToUse}
                          finalPrice={finalPrice}
                          creditsEarned={calculateCreditsEarned(finalPrice)}
                        />
                      </motion.div>

                      {/* Show credit section if user has any credits */}
                      {userCredits > 0 && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.6, duration: 0.6 }}
                        >
                          <CreditDiscountSection
                            userCredits={userCredits}
                            maxCreditsUsable={maxCreditsUsable}
                            creditsToUse={creditsToUse}
                            onCreditsChange={setCreditsToUse}
                          />
                        </motion.div>
                      )}

                      {/* Action Buttons */}
                      <motion.div 
                        className="space-y-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.8, duration: 0.6 }}
                      >
                        <PaymentButtons
                          onPurchase={onPurchase}
                          price={finalPrice}
                          isEarlyAccess={purchaseType === 'early_access'}
                          bundleId={bundleId}
                          earlyAccessBookId={earlyAccessBookId}
                          bundleTitle={getModalTitle()}
                          bundleType={bundle?.bundle_type || 'temporal'}
                          creditsToUse={creditsToUse}
                        />
                        
                        <Button
                          variant="outline"
                          onClick={onClose}
                          className="w-full border-white/20 text-white/70 hover:text-white hover:bg-white/10"
                        >
                          Cancel
                        </Button>
                      </motion.div>

                      {/* Security Note - with extra bottom padding */}
                      <motion.div 
                        className="text-center text-white/50 text-xs pb-8"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.0, duration: 0.6 }}
                      >
                        <p>🔒 Secure payment powered by Stripe</p>
                        <p>Your payment information is never stored on our servers</p>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
}
